import React from "react";

import { useNavigate } from "react-router-dom";

import _ from "lodash";

import ActionsTableCell from "commons/components/ActionsTableCell";

import useAlertConfirm, { AlertConfirmHookType } from "commons/hooks/useAlertConfirm";
import { DataTableHook } from "commons/hooks/useDataTable";
import useDataTableWithMemory from "commons/hooks/useDataTableWithMemory";
import useDateFormat from "commons/hooks/useDateFormat";
import { StaticTableFilterHook } from "commons/hooks/useStaticTableFilter";
import useStaticTableFilterWithMemory from "commons/hooks/useStaticTableFilterWithMemory";
import useTableFilterWithMemoryAndButtons, {
    TableFilterWithMemoryAndButtonsHook,
} from "commons/hooks/useTableFilterWithMemoryAndButtons";

import {
    Column,
    FilterFieldCollection,
    FilterOperation,
    FilterType,
    SortDirection,
} from "commons/types/table";

import { calculateRows } from "commons/ui/datatable/TableRows";
import HtmlWrapper from "commons/ui/HtmlWrapper";

import { createUnixMilliFromLocalDate } from "commons/utils/moment";

import useWorkApi from "hooks/api/private/useWorkApi";
import useSnackbar from "hooks/useSnackbar";

import { WorkListItem } from "types/api/work";

export type WorkOffersListHook = {
    navigateToNewWorkOffer: () => void;
    onSearchFilterChange: (
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    ) => void;
    dataTableHook: DataTableHook<WorkListItem>;
    filterSearchHook: TableFilterWithMemoryAndButtonsHook;
    staticFilterHook: StaticTableFilterHook;
    alertConfirmHook: AlertConfirmHookType;
};

function useWorkOffersList(): WorkOffersListHook {
    const { enqueueSuccessSnackbar, enqueueErrorSnackbar } = useSnackbar();

    const memoizedFieldsForSearch = React.useMemo<FilterFieldCollection>(
        () => ({
            search: {
                operation: FilterOperation.Or,
                fields: {
                    title: {
                        key: "title",
                        operation: FilterOperation.Like,
                        defaultValue: "",
                    },
                    slug: {
                        key: "slug",
                        operation: FilterOperation.Like,
                        defaultValue: "",
                    },
                    full_description: {
                        key: "full_description",
                        operation: FilterOperation.Like,
                        defaultValue: "",
                    },
                    short_description: {
                        key: "short_description",
                        operation: FilterOperation.Like,
                        defaultValue: "",
                    },
                },
            },
        }),
        [],
    );

    const filterSearchHook = useTableFilterWithMemoryAndButtons(
        "work-offers-list-search",
        memoizedFieldsForSearch,
    );

    const { setForm: setFiltersForm } = filterSearchHook;

    const onSearchFilterChange = React.useCallback(
        (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
            setFiltersForm((prevForm) => {
                const newForm = _.cloneDeep(prevForm);

                const value = "" + event.target.value;

                newForm.search.title = value;
                newForm.search.slug = value;
                newForm.search.full_description = value;
                newForm.search.short_description = value;

                return newForm;
            }),
        [setFiltersForm],
    );

    const memoizedFieldsForStaticFilters = React.useMemo(
        () => ({
            enabled: {
                key: "enabled",
                operation: FilterOperation.Eq,
                defaultValue: "",
            },
        }),
        [],
    );

    const staticFilterHook = useStaticTableFilterWithMemory(
        "work-offers-list-static",
        memoizedFieldsForStaticFilters,
    );

    const { getAllWork, deleteWork } = useWorkApi();
    const { formatDate } = useDateFormat();

    const navigate = useNavigate();

    const navigateToEditWorkOffer = React.useCallback(
        (row: WorkListItem) => navigate(`${row.id}/modifica-offerta`),
        [navigate],
    );

    const [triggerReloadTable, setTriggerReloadTable] = React.useState<boolean>(false);

    const alertConfirmHook = useAlertConfirm({
        title: "Elimina offerta di lavoro",
        cancelButtonLabel: "Annulla",
        confirmButtonLabel: "Conferma",
    });

    const openConfirmAlert = React.useMemo(() => alertConfirmHook.open, [alertConfirmHook.open]);

    const deleteWorkButton = React.useCallback(
        (row: WorkListItem) => {
            openConfirmAlert("Sei sicuro di voler eliminare l'offerta di lavoro?", () => {
                const [apiCall] = deleteWork({ id: row.id });

                apiCall
                    .then(() => {
                        setTriggerReloadTable(true);
                        enqueueSuccessSnackbar("Offerta di lavoro eliminata con successo");
                    })
                    .catch(() =>
                        enqueueErrorSnackbar("Errore nell'eliminazione dell'offerta di lavoro"),
                    );
            });
        },
        [
            enqueueSuccessSnackbar,
            setTriggerReloadTable,
            enqueueErrorSnackbar,
            openConfirmAlert,
            deleteWork,
        ],
    );

    const columns: Array<Column<WorkListItem>> = React.useMemo(
        () => [
            {
                id: "date",
                header: "Data",
                width: "90px",
                orderable: true,
                render: (row: WorkListItem) => formatDate(createUnixMilliFromLocalDate(row.date)),
            },
            {
                id: "title",
                header: "Titolo",
                render: (row: WorkListItem) => <HtmlWrapper htmlString={row.title} />,
            },
            {
                id: "enabled",
                header: "Area pubblica",
                width: "130px",
                render: (row: WorkListItem) => (row.enabled ? "Visibile" : "Non visibile"),
            },
            {
                id: "action",
                header: "Azioni",
                headerAlign: "center",
                align: "center",
                width: "100px",
                render: (row: WorkListItem) => (
                    <ActionsTableCell
                        row={row}
                        onEdit={navigateToEditWorkOffer}
                        onDelete={deleteWorkButton}
                    />
                ),
            },
        ],
        [navigateToEditWorkOffer, deleteWorkButton, formatDate],
    );

    const { filters: searchFilters } = filterSearchHook;
    const { filters: staticFilters } = staticFilterHook;

    const filters: FilterType[] = React.useMemo(
        () => [...searchFilters, ...staticFilters],
        [searchFilters, staticFilters],
    );

    const dataTableHook = useDataTableWithMemory<WorkListItem>({
        apiCall: getAllWork,
        getElements: calculateRows,
        columns: columns,
        filters: filters,
        sortDefault: {
            col: "date",
            dir: SortDirection.Desc,
        },
        size: 20,
        key: "work-offers-list",
    });

    const { loadTableData } = dataTableHook;

    React.useEffect(() => {
        if (triggerReloadTable) {
            loadTableData();

            setTriggerReloadTable(false);
        }
    }, [loadTableData, triggerReloadTable]);

    const navigateToNewWorkOffer = React.useCallback(() => {
        navigate("/lavora-con-noi/nuova-offerta");
    }, [navigate]);

    return React.useMemo(
        (): WorkOffersListHook => ({
            onSearchFilterChange,
            navigateToNewWorkOffer,
            filterSearchHook,
            staticFilterHook,
            alertConfirmHook,
            dataTableHook,
        }),
        [
            onSearchFilterChange,
            navigateToNewWorkOffer,
            filterSearchHook,
            alertConfirmHook,
            staticFilterHook,
            dataTableHook,
        ],
    );
}

export default useWorkOffersList;
